import React from 'react';
import { Link } from 'react-router-dom';

import Layout from '../components/layout/Layout';

// Export empty object to ensure file is treated as a module
export {};

const FAQ: React.FC = () => {
  return (
    <Layout>
      <div className="bg-gradient-to-br from-dark to-dark-dark text-white py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold mb-4 text-primary">Frequently Asked Questions</h1>
            <p className="text-xl max-w-3xl mx-auto">
              Find answers to common questions about SpinDom and how to make the most of your
              experience.
            </p>
          </div>

          <div className="max-w-4xl mx-auto bg-dark-light rounded-lg shadow-xl overflow-hidden mb-12">
            <div className="p-8">
              <h2 className="text-2xl font-bold mb-6 text-primary">General Questions</h2>

              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-semibold mb-2">What is SpinDom?</h3>
                  <p className="text-gray-300 mb-4">
                    SpinDom is a platform that lets you create customizable spinning wheels for
                    various purposes. Whether you&apos;re looking to make decisions, assign tasks,
                    or create interactive experiences, our wheels provide a fun and engaging way to
                    do it.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-2">
                    Do I need an account to use SpinDom?
                  </h3>
                  <p className="text-gray-300 mb-4">
                    No, you don&apos;t need an account to create and use basic wheels. Without an
                    account, your wheels will be saved in your browser&apos;s local storage and will
                    be lost if you clear your browser data. For cloud saving and additional
                    features, you can create a free account to access the full functionality.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-2">How do I create a wheel?</h3>
                  <p className="text-gray-300 mb-4">Creating a wheel is simple:</p>
                  <ol className="list-decimal list-inside space-y-2 text-gray-300 ml-4">
                    <li>Click on &quot;Create Wheel&quot; in the navigation menu</li>
                    <li>Give your wheel a name</li>
                    <li>Add entries (items that will appear on your wheel)</li>
                    <li>Customize colors and settings if desired</li>
                    <li>Save your wheel</li>
                  </ol>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-2">How do I spin a wheel?</h3>
                  <p className="text-gray-300 mb-4">
                    Once your wheel is created, simply click the &quot;Spin Wheel&quot; button. The
                    wheel will rotate and eventually stop on a random entry. The result will be
                    displayed in a popup that you can dismiss or use to spin again.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="max-w-4xl mx-auto bg-dark-light rounded-lg shadow-xl overflow-hidden mb-12">
            <div className="p-8">
              <h2 className="text-2xl font-bold mb-6 text-primary">User Tiers</h2>

              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-semibold mb-2">What user tiers do you offer?</h3>
                  <p className="text-gray-300 mb-4">We offer two user tiers:</p>
                  <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
                    <li>
                      <strong className="text-gray-200">Guest (Free):</strong> Our basic tier that
                      requires no account. Create unlimited wheels with basic customization, but
                      they&apos;re only saved in your browser.
                    </li>
                    <li>
                      <strong className="text-gray-200">Signed-In (Free):</strong> Create a free
                      account to save unlimited wheels to your account, access them from any device,
                      share wheels with spin tracking, and view spin history.
                    </li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-2">
                    What are the benefits of creating an account?
                  </h3>
                  <p className="text-gray-300 mb-4">
                    With a free account, you get several advantages over the guest experience:
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
                    <li>Save your wheels to the cloud and access them from any device</li>
                    <li>Share wheels with others and track spins</li>
                    <li>View spin history and statistics</li>
                    <li>Access additional customization options</li>
                    <li>Create and manage multiple wheels easily</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-2">How do I create an account?</h3>
                  <p className="text-gray-300 mb-4">Creating an account is simple and free:</p>
                  <ol className="list-decimal list-inside space-y-2 text-gray-300 ml-4">
                    <li>Click on &quot;Sign Up&quot; in the navigation menu</li>
                    <li>Enter your email address</li>
                    <li>Create a password</li>
                    <li>Verify your email address</li>
                    <li>Start enjoying all the benefits of a signed-in user</li>
                  </ol>
                </div>
              </div>
            </div>
          </div>

          <div className="max-w-4xl mx-auto bg-dark-light rounded-lg shadow-xl overflow-hidden mb-12">
            <div className="p-8">
              <h2 className="text-2xl font-bold mb-6 text-primary">Sharing & Tracking</h2>

              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-semibold mb-2">
                    How do I share a wheel with someone?
                  </h3>
                  <p className="text-gray-300 mb-4">When logged in, you can share your wheels:</p>
                  <ol className="list-decimal list-inside space-y-2 text-gray-300 ml-4">
                    <li>Navigate to &quot;My Wheels&quot; in your account</li>
                    <li>Select the wheel you want to share</li>
                    <li>Click the &quot;Share&quot; button</li>
                    <li>Configure sharing options (spin limit, expiration date, etc.)</li>
                    <li>Click &quot;Create Share Link&quot; to generate a unique URL</li>
                    <li>Copy and share the link with anyone you want</li>
                  </ol>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-2">
                    What is spin tracking and how does it work?
                  </h3>
                  <p className="text-gray-300 mb-4">
                    Spin tracking records each spin of your shared wheel, including:
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
                    <li>The result of each spin</li>
                    <li>Timestamp of when the spin occurred</li>
                    <li>Who spun the wheel (if they were logged in)</li>
                    <li>Statistics on which segments were landed on most frequently</li>
                  </ul>
                  <p className="text-gray-300 mt-2">
                    You can view all this information in the shared wheel dashboard, accessible from
                    your &quot;My Wheels&quot; section.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-2">
                    Can I limit how many times a shared wheel can be spun?
                  </h3>
                  <p className="text-gray-300 mb-4">
                    Yes, when sharing a wheel, you can set a spin limit (maximum number of spins
                    allowed). Once this limit is reached, the wheel can no longer be spun unless you
                    increase the limit. You can also set an expiration date for the share link.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-2">How do I manage my shared wheels?</h3>
                  <p className="text-gray-300 mb-4">
                    You have full control over your shared wheels:
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
                    <li>View all active share links in your dashboard</li>
                    <li>Deactivate a share link at any time</li>
                    <li>Extend or reduce the expiration date</li>
                    <li>Increase or decrease the spin limit</li>
                    <li>Create multiple share links for the same wheel with different settings</li>
                    <li>Delete share links permanently</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <div className="max-w-4xl mx-auto bg-dark-light rounded-lg shadow-xl overflow-hidden">
            <div className="p-8">
              <h2 className="text-2xl font-bold mb-6 text-primary">Account & Privacy</h2>

              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-semibold mb-2">How secure is my data?</h3>
                  <p className="text-gray-300 mb-4">We take your privacy and security seriously:</p>
                  <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
                    <li>Your wheels are private by default</li>
                    <li>Only people with your share links can access shared wheels</li>
                    <li>We use secure authentication and data storage</li>
                    <li>
                      All sensitive information is transmitted using secure socket layer (SSL)
                      technology
                    </li>
                    <li>You can delete your data at any time</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-2">
                    How do I update my account information?
                  </h3>
                  <p className="text-gray-300 mb-4">
                    You can update your account information, including your display name, email
                    address, and password. We recommend changing your password regularly for
                    security.
                  </p>
                  <ol className="list-decimal list-inside space-y-2 text-gray-300 ml-4">
                    <li>Log in to your account</li>
                    <li>Go to your Profile or Account Settings</li>
                    <li>Update your information</li>
                    <li>Save your changes</li>
                  </ol>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-2">How do I delete my account?</h3>
                  <p className="text-gray-300 mb-4">To delete your account:</p>
                  <ol className="list-decimal list-inside space-y-2 text-gray-300 ml-4">
                    <li>Log in to your account</li>
                    <li>Go to your Profile or Account Settings</li>
                    <li>Scroll to the bottom and find &quot;Delete Account&quot;</li>
                    <li>Follow the confirmation steps</li>
                  </ol>
                  <p className="text-gray-300 mt-2">
                    Please note that account deletion is permanent and will remove all your saved
                    wheels, share links, and spin history. This action cannot be undone.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center mt-12">
            <h2 className="text-2xl font-bold mb-4">Still Have Questions?</h2>
            <p className="text-gray-300 mb-6 max-w-2xl mx-auto">
              If you couldn&apos;t find the answer you were looking for, please reach out to our
              support team.
            </p>
            <Link to="/contact" className="btn-primary px-8 py-3 text-lg">
              Contact Support
            </Link>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default FAQ;
